// src/components/products/ProductVariantForm.js
import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { partTypeService } from '../../services/api';
import { toast } from 'react-toastify';

const ProductVariantForm = ({ isOpen, onClose, onSave, productId }) => {
    const [existingPartTypes, setExistingPartTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedOption, setSelectedOption] = useState('existing'); // 'existing' or 'new'

    // For existing part type
    const [selectedPartTypeId, setSelectedPartTypeId] = useState('');
    const [quantityRequired, setQuantityRequired] = useState(1);
    const [variantName, setVariantName] = useState('');

    // For new part type
    const [newPartTypeData, setNewPartTypeData] = useState({
                                                               name: '',
                                                               description: '',
                                                               is_sku_specific: true, // Default to true for product variants
                                                               slug: '',
                                                               threshold: 0,
                                                           });

    useEffect(() => {
        if (isOpen) {
            fetchPartTypes();
        }
    }, [isOpen]);

    const fetchPartTypes = async () => {
        try {
            setLoading(true);
            const response = await partTypeService.getPartTypes();

            // Filter to show only part types that could be variants
            // You might want to adjust this filter based on your needs
            const filteredPartTypes = response.data.filter(
                pt => pt.slug !== 'standard' && pt.slug !== 'carbon'
            );

            setExistingPartTypes(filteredPartTypes);
        } catch (err) {
            console.error('Error fetching part types:', err);
            toast.error('Failed to load part types');
        } finally {
            setLoading(false);
        }
    };

    const handleExistingPartTypeChange = (e) => {
        setSelectedPartTypeId(e.target.value);
    };

    const handleNewPartTypeDataChange = (e) => {
        const { name, value } = e.target;
        setNewPartTypeData({
                               ...newPartTypeData,
                               [name]: value,
                           });

        // Auto-generate slug when name changes
        if (name === 'name') {
            setNewPartTypeData(prev => ({
                ...prev,
                slug: value.toLowerCase().replace(/\s+/g, '_')
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (selectedOption === 'existing') {
                // Use existing part type
                if (!selectedPartTypeId) {
                    toast.error('Please select a part type');
                    return;
                }

                if (!variantName) {
                    toast.error('Please provide a variant name');
                    return;
                }

                // Format data according to expected payload structure
                const partRequirementData = {
                    part_type_id: parseInt(selectedPartTypeId, 10),
                    quantity_required: parseInt(quantityRequired, 10),
                    variant: variantName.toLowerCase()
                };

                onSave(partRequirementData);
            } else {
                // Create new part type first, then add it as a requirement
                if (!newPartTypeData.name || !newPartTypeData.slug) {
                    toast.error('Part type name and slug are required');
                    return;
                }

                // Create the new part type
                const partTypeResponse = await partTypeService.createPartType({
                                                                                  ...newPartTypeData,
                                                                                  is_sku_specific: true
                                                                              });

                if (!partTypeResponse.data || !partTypeResponse.data) {
                    throw new Error('Failed to create part type');
                }

                const newPartTypeId = partTypeResponse.data.id;

                // Format data according to expected payload structure
                const partRequirementData = {
                    part_type_id: newPartTypeId,
                    quantity_required: parseInt(quantityRequired, 10),
                    variant: newPartTypeData.slug
                };

                onSave(partRequirementData);

                // Refresh part types list
                fetchPartTypes();
            }

            onClose();
        } catch (err) {
            console.error('Error saving variant:', err);
            toast.error(err.response?.data?.errors
                        ? Object.values(err.response.data.errors).flat().join(', ')
                        : 'Error saving variant');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
                <div className="flex justify-between items-center px-6 py-4 border-b">
                    <h3 className="text-lg font-medium text-gray-900">
                        Add Part Type Variant
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500"
                    >
                        <XMarkIcon className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="px-6 py-4 space-y-4">
                        {/* Option selector */}
                        <div className="flex items-center space-x-4">
                            <div className="flex items-center">
                                <input
                                    id="existing-option"
                                    name="part-type-option"
                                    type="radio"
                                    checked={selectedOption === 'existing'}
                                    onChange={() => setSelectedOption('existing')}
                                    className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300"
                                />
                                <label htmlFor="existing-option" className="ml-2 block text-sm font-medium text-gray-700">
                                    Use Existing Part Type
                                </label>
                            </div>
                            <div className="flex items-center">
                                <input
                                    id="new-option"
                                    name="part-type-option"
                                    type="radio"
                                    checked={selectedOption === 'new'}
                                    onChange={() => setSelectedOption('new')}
                                    className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300"
                                />
                                <label htmlFor="new-option" className="ml-2 block text-sm font-medium text-gray-700">
                                    Create New Part Type
                                </label>
                            </div>
                        </div>

                        {selectedOption === 'existing' ? (
                            <div className="space-y-4">
                                {/* Existing Part Type selector */}
                                <div>
                                    <label htmlFor="part_type_id" className="block text-sm font-medium text-gray-700">
                                        Part Type <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        id="part_type_id"
                                        name="part_type_id"
                                        value={selectedPartTypeId}
                                        onChange={handleExistingPartTypeChange}
                                        required
                                        className="mt-1 p-3 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                                    >
                                        <option value="">Select a part type</option>
                                        {existingPartTypes.map(partType => (
                                            <option key={partType.id} value={partType.id}>
                                                {partType.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Variant Name */}
                                <div>
                                    <label htmlFor="variant_name" className="block text-sm font-medium text-gray-700">
                                        Variant Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="variant_name"
                                        name="variant_name"
                                        value={variantName}
                                        onChange={(e) => setVariantName(e.target.value)}
                                        required
                                        placeholder="e.g., custom, premium, leather"
                                        className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md p-2"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        This will be used to identify the variant in the system
                                    </p>
                                </div>
                            </div>
                        ) : (
                             <div className="space-y-4">
                                 {/* Create New Part Type form */}
                                 <div>
                                     <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                         Part Type Name <span className="text-red-500">*</span>
                                     </label>
                                     <input
                                         type="text"
                                         id="name"
                                         name="name"
                                         value={newPartTypeData.name}
                                         onChange={handleNewPartTypeDataChange}
                                         required
                                         className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md p-2"
                                     />
                                 </div>

                                 <div>
                                     <label htmlFor="slug" className="block text-sm font-medium text-gray-700">
                                         Slug <span className="text-red-500">*</span>
                                     </label>
                                     <input
                                         type="text"
                                         id="slug"
                                         name="slug"
                                         value={newPartTypeData.slug}
                                         onChange={handleNewPartTypeDataChange}
                                         required
                                         className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md p-2"
                                     />
                                     <p className="mt-1 text-xs text-gray-500">
                                         This will also be used as the variant name
                                     </p>
                                 </div>

                                 <div>
                                     <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                         Description
                                     </label>
                                     <textarea
                                         id="description"
                                         name="description"
                                         rows="2"
                                         value={newPartTypeData.description}
                                         onChange={handleNewPartTypeDataChange}
                                         className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md p-2"
                                     />
                                 </div>

                                 <div>
                                     <label htmlFor="threshold" className="block text-sm font-medium text-gray-700">
                                         Threshold
                                     </label>
                                     <input
                                         type="number"
                                         id="threshold"
                                         name="threshold"
                                         min="0"
                                         value={newPartTypeData.threshold}
                                         onChange={handleNewPartTypeDataChange}
                                         className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md p-2"
                                     />
                                     <p className="mt-1 text-xs text-gray-500">
                                         Minimum stock level before reordering
                                     </p>
                                 </div>
                             </div>
                         )}

                        {/* Quantity Required - for both options */}
                        <div>
                            <label htmlFor="quantity_required" className="block text-sm font-medium text-gray-700">
                                Quantity Required <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                id="quantity_required"
                                name="quantity_required"
                                min="1"
                                value={quantityRequired}
                                onChange={(e) => setQuantityRequired(parseInt(e.target.value, 10) || 1)}
                                required
                                className="mt-1 p-3 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md p-2"
                            />
                        </div>
                    </div>

                    <div className="px-6 py-4 border-t flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="bg-primary-600 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                            Add Variant
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ProductVariantForm;
